/**
 * Background video controller (YouTube / Vimeo / MP4).
 *
 * The shared background renderer outputs DEFERRED placeholders so heavy embeds
 * never load until they scroll into view (IntersectionObserver):
 *   - YouTube: div[data-yt-bg]                 -> player built via the IFrame API, looped by
 *              seeking back BEFORE the end so YouTube's end screen never appears.
 *   - Vimeo:   iframe[data-vimeo-bg][data-src] -> src set when in view, controlled via Player SDK.
 *   - MP4:     video[data-mp4-bg][data-src]    -> source set + played when in view.
 *
 * When a video is present the renderer also outputs a `.background-video-controls`
 * group (play/pause + mute). Each video type exposes a uniform controller
 * { play, pause, setMuted } that those buttons drive.
 *
 * Playback options (autoplay / loop / mute) come from the block fields via data
 * attributes. Self-initialising side-effect module (imported from javascripts/index.js).
 */

const YT_SELECTOR = '[data-yt-bg]';
const VIMEO_SELECTOR = 'iframe[data-vimeo-bg]';
const MP4_SELECTOR = 'video[data-mp4-bg]';
const YT_API_SRC = 'https://www.youtube.com/iframe_api';
const VIMEO_API_SRC = 'https://player.vimeo.com/api/player.js';
const LOOP_LEAD = 0.6; // seconds before the end to restart, pre-empting YouTube's end screen

// ── API loaders (each script loaded once, callbacks queued) ──────────────────
let ytLoading = false;
const ytPending = [];
function whenYouTubeApiReady(cb) {
  if (window.YT && window.YT.Player) {
    cb();
    return;
  }
  ytPending.push(cb);
  if (ytLoading) return;
  ytLoading = true;

  const prevReady = window.onYouTubeIframeAPIReady;
  window.onYouTubeIframeAPIReady = () => {
    if (typeof prevReady === 'function') prevReady();
    ytPending.splice(0).forEach((fn) => fn());
  };
  const tag = document.createElement('script');
  tag.src = YT_API_SRC;
  document.head.appendChild(tag);
}

let vimeoLoading = false;
const vimeoPending = [];
function whenVimeoApiReady(cb) {
  if (window.Vimeo && window.Vimeo.Player) {
    cb();
    return;
  }
  vimeoPending.push(cb);
  if (vimeoLoading) return;
  vimeoLoading = true;

  const tag = document.createElement('script');
  tag.src = VIMEO_API_SRC;
  tag.onload = () => vimeoPending.splice(0).forEach((fn) => fn());
  document.head.appendChild(tag);
}

// ── Per-type activation → uniform controller { play, pause, setMuted } ────────
function activateYouTube(el, onReady) {
  if (el.dataset.ytBgReady) return; // idempotent
  el.dataset.ytBgReady = '1';

  const videoId = el.dataset.ytId;
  if (!videoId) return;

  const autoplay = el.dataset.ytAutoplay !== '0';
  const loop = el.dataset.ytLoop !== '0';
  const mute = el.dataset.ytMute !== '0';

  let guard = null;
  const clearGuard = () => {
    if (guard) {
      clearInterval(guard);
      guard = null;
    }
  };

  whenYouTubeApiReady(() => {
    // eslint-disable-next-line no-new
    new window.YT.Player(el, {
      videoId,
      width: '100%',
      height: '100%',
      playerVars: {
        autoplay: autoplay ? 1 : 0,
        mute: mute ? 1 : 0,
        controls: 0,
        rel: 0,
        modestbranding: 1,
        playsinline: 1,
        disablekb: 1,
        fs: 0,
        iv_load_policy: 3,
        showinfo: 0,
      },
      events: {
        onReady: (e) => {
          // The API replaces our div with a fresh iframe; reapply positioning + a11y.
          const iframe = e.target.getIframe();
          iframe.classList.add('background-video', 'pointer-events-none');
          iframe.setAttribute('title', 'Background video');
          iframe.setAttribute('aria-hidden', 'true');
          if (mute) e.target.mute();
          else e.target.unMute();
          if (autoplay) e.target.playVideo();

          onReady({
            play: () => e.target.playVideo(),
            pause: () => e.target.pauseVideo(),
            setMuted: (m) => (m ? e.target.mute() : e.target.unMute()),
          });
        },
        onStateChange: (e) => {
          const player = e.target;
          const { PLAYING, ENDED } = window.YT.PlayerState;
          if (e.data === PLAYING && loop) {
            // Poll while playing; restart just before the end screen can appear.
            clearGuard();
            guard = setInterval(() => {
              const duration = player.getDuration ? player.getDuration() : 0;
              const current = player.getCurrentTime ? player.getCurrentTime() : 0;
              if (duration > 0 && current >= duration - LOOP_LEAD) {
                player.seekTo(0, true); // stays in PLAYING — no end screen, no seam flash
              }
            }, 200);
          } else if (e.data === ENDED && loop) {
            clearGuard();
            player.seekTo(0, true);
            player.playVideo();
          }
        },
      },
    });
  });
}

function activateVimeo(iframe, onReady) {
  if (iframe.dataset.bgReady || !iframe.dataset.src) return;
  iframe.dataset.bgReady = '1';
  iframe.src = iframe.dataset.src; // autoplay/loop/mute baked into the query by the renderer

  whenVimeoApiReady(() => {
    const player = new window.Vimeo.Player(iframe);
    onReady({
      play: () => player.play().catch(() => {}),
      pause: () => player.pause().catch(() => {}),
      setMuted: (m) => player.setMuted(m).catch(() => {}),
    });
  });
}

function activateMp4(video, onReady) {
  if (video.dataset.bgReady || !video.dataset.src) return;
  video.dataset.bgReady = '1';
  video.src = video.dataset.src;
  video.load();
  if (video.dataset.autoplay !== '0') {
    const playing = video.play();
    if (playing && typeof playing.catch === 'function') playing.catch(() => {});
  }
  onReady({
    play: () => {
      const playing = video.play();
      if (playing && typeof playing.catch === 'function') playing.catch(() => {});
    },
    pause: () => video.pause(),
    setMuted: (m) => {
      video.muted = m;
    },
  });
}

// ── Controls (play/pause + mute) ─────────────────────────────────────────────
function findControls(el) {
  const wrapper = el.closest('.background-video-wrapper');
  const scope = (wrapper && wrapper.parentElement) || el.parentElement;
  return scope ? scope.querySelector('.background-video-controls') : null;
}

function setIcon(btn, onClass, offClass, on) {
  const icon = btn.querySelector('.icomoon');
  if (!icon) return;
  icon.classList.toggle(onClass, on);
  icon.classList.toggle(offClass, !on);
}

function wireControls(controls, ctrl) {
  const toggleBtn = controls.querySelector('.js-bgv-toggle');
  const muteBtn = controls.querySelector('.js-bgv-mute');
  // Initial state mirrors the rendered fields (autoplay / mute).
  let playing = controls.dataset.playing !== '0';
  let muted = controls.dataset.muted !== '0';

  if (toggleBtn) {
    toggleBtn.addEventListener('click', () => {
      playing = !playing;
      if (playing) ctrl.play();
      else ctrl.pause();
      setIcon(toggleBtn, 'icon-pause', 'icon-play', playing);
      toggleBtn.setAttribute('aria-label', playing ? 'Pause video' : 'Play video');
    });
  }

  if (muteBtn) {
    muteBtn.addEventListener('click', () => {
      muted = !muted;
      ctrl.setMuted(muted);
      setIcon(muteBtn, 'icon-volume_off', 'icon-volume_up', muted);
      muteBtn.setAttribute('aria-label', muted ? 'Unmute video' : 'Mute video');
    });
  }
}

// ── Lazy activation ──────────────────────────────────────────────────────────
function activate(el, onReady) {
  if (el.matches(YT_SELECTOR)) activateYouTube(el, onReady);
  else if (el.matches(VIMEO_SELECTOR)) activateVimeo(el, onReady);
  else if (el.matches(MP4_SELECTOR)) activateMp4(el, onReady);
}

function lazy(el, run) {
  if (!('IntersectionObserver' in window)) {
    run(el);
    return;
  }
  const io = new IntersectionObserver((entries, obs) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      obs.unobserve(entry.target);
      run(entry.target);
    });
  }, { rootMargin: '200px' });
  io.observe(el);
}

function init() {
  const selector = `${YT_SELECTOR}, ${VIMEO_SELECTOR}, ${MP4_SELECTOR}`;
  document.querySelectorAll(selector).forEach((el) => {
    // Skip elements owned by the Full Width Video module (data-module="VideoPlayer"):
    // it runs its own player with native controls enabled. Both share the
    // `ytBgReady` flag, so whichever activates first wins — and this global helper
    // (a direct import) would otherwise beat the module's lazy import and build a
    // chrome-less controls:0 player, hiding the YouTube controls.
    if (el.closest('[data-module="VideoPlayer"]')) return;
    const controls = findControls(el);
    const onReady = controls ? (ctrl) => wireControls(controls, ctrl) : () => {};
    lazy(el, (target) => activate(target, onReady));
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
